import React, { useState, useEffect, useRef, ChangeEvent } from "react";
import classNames from "classnames";
import styles from "./Field.module.scss";
import mp from "./FieldMediaPendingImage.module.scss";
import Icon from "../images/Icon";
import { Toggle } from "./Toggle";
import { getMediaType } from "./FieldMedia";

export interface MediaOverlayText {
  text      : string;
  visible   : boolean;
  font      : string;
  color     : string;
  x         : number;  // position en pixels, exprimée sur le canvas de référence (referenceWidth × referenceHeight)
  y         : number;
  size      : number;  // taille de police, en pixels, sur le même canvas de référence
  boxOpacity?: number; // opacité (0-100 %) du fond sombre derrière le texte sur la mire ; 0 ou absent = aucun
  bold?      : boolean;
  underline? : boolean;
  italic?    : boolean;
  onMove?   : (x: number, y: number) => void;
  onResize? : (size: number) => void;
}

type DragState = {
  key            : 'title' | 'date';
  mode           : 'move' | 'resize';
  startClientX   : number;
  startClientY   : number;
  startX         : number;
  startY         : number;
  startSize      : number;
  onMove?        : (x: number, y: number) => void;
  onResize?      : (size: number) => void;
};

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

export interface FieldMediaPendingImageProps {
  placeholder?        : string;
  value?              : string | number;
  readOnly?           : boolean;
  onChange?           : (event: React.ChangeEvent<HTMLInputElement>, value: string | number | File) => void;
  /** Appelé quand le fichier choisi n'est pas une image : il est refusé avant tout aperçu,
      et rien n'est remonté par `onChange`. */
  onFileRejected?     : (file: File) => void;
  onDelete?           : () => void;
  onPreviewClick?     : () => void;
  width?              : string;
  height?             : string;
  glowColor?          : string;
  iconSquareColor?    : string;
  previewWidth?       : string;
  previewHeight?      : string;
  previewFit?         : 'cover' | 'contain';
  baseURL?            : string;
  extension?          : string;
  showPreview?        : boolean;
  previewAlwaysShown? : boolean;
  previewPosition?    : 'bottom' | 'left_top' | 'left_center' | 'left_bottom' | 'right_top' | 'right_center' | 'right_bottom';
  previewClickable?   : boolean;
  upload?             : boolean;
  showTextInput?      : boolean;
  defaultImage?       : string;
  /** Nom du fichier tel qu'il a été téléversé, affiché sur la ligne des icônes. */
  fileName?           : string;
  title               : MediaOverlayText;
  date                : MediaOverlayText;
  referenceWidth?     : number;
  referenceHeight?    : number;
  // Toggles "visible" du nom / de la date, affichés sous les icônes upload/suppression, à côté de l'aperçu.
  // Non affichés si les callbacks correspondants ne sont pas fournis.
  onTitleVisibleChange? : (visible: boolean) => void;
  onDateVisibleChange?  : (visible: boolean) => void;
  titleToggleLabel?     : string;
  dateToggleLabel?      : string;
  // Élément actuellement sélectionné (contour + ancre de redimensionnement affichés, réglages
  // associés affichés côté appelant). Contrôlé par le parent : au plus un élément sélectionné à la fois.
  selectedOverlay?          : 'title' | 'date' | null;
  onSelectedOverlayChange?  : (key: 'title' | 'date' | null) => void;
  /** 'side' (défaut, disposition d'origine) ou 'stacked' : aperçu en cadre de contrôle, pastilles
      Nom / Heure, repères, boutons Remplacer / Supprimer sous l'aperçu. */
  layout?                   : 'side' | 'stacked';
  /** Aimante le Nom / l'Heure sur le centre de l'aperçu pendant le glisser. Par défaut : en 'stacked'. */
  snapToGuides?             : boolean;
}

export const FieldMediaPendingImage = ({
  placeholder,
  value = "",
  readOnly,
  onChange,
  onFileRejected,
  onDelete,
  onPreviewClick,
  width = "100%",
  height = "fit-content",
  glowColor = "var(--color-Field-Focus-normal)",
  iconSquareColor = "var(--color-Field-Border-normal)",
  previewWidth,
  previewHeight,
  previewFit = 'contain',
  baseURL = "",
  extension = "",
  showPreview = true,
  previewAlwaysShown = false,
  previewPosition = 'bottom',
  previewClickable = true,
  upload = true,
  showTextInput = true,
  defaultImage,
  fileName,
  title,
  date,
  referenceWidth = 1920,
  referenceHeight = 1080,
  onTitleVisibleChange,
  onDateVisibleChange,
  titleToggleLabel = "Nom",
  dateToggleLabel = "Date",
  selectedOverlay = null,
  onSelectedOverlayChange,
  layout = 'side',
  snapToGuides,
}: FieldMediaPendingImageProps) => {
  const stacked = layout === 'stacked';
  const snapEnabled = snapToGuides ?? stacked;

  const fieldStyle = {
    "--glow-color": glowColor,
    "--icon-square-color": iconSquareColor,
  } as React.CSSProperties;

  const [mediaSrc, setMediaSrc] = useState("");
  const [inputValue, setInputValue] = useState<string | number>(value);
  const [isBlob, setIsBlob] = useState(false);
  const [mediaError, setMediaError] = useState(false);
  const [dragging, setDragging] = useState<DragState | null>(null);
  const [showGuides, setShowGuides] = useState(false);
  const [snap, setSnap] = useState<{ x: boolean; y: boolean } | null>(null);
  // Largeur réellement affichée de l'aperçu : en 'stacked' il s'adapte à sa carte, la taille des textes
  // doit suivre cette largeur et non plus une largeur fixée à l'avance.
  const [measuredWidth, setMeasuredWidth] = useState(0);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const blobUrlRef = useRef<string | null>(null);
  const fileInputId = useRef(`file-input-${Math.random().toString(36).slice(2, 11)}`);
  const previewBoxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!stacked) return;
    const el = previewBoxRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(entries => setMeasuredWidth(entries[0]?.contentRect.width ?? 0));
    ro.observe(el);
    setMeasuredWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, [stacked, mediaSrc, mediaError]);

  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (value) {
      setInputValue(value);
      if (typeof value === "string") {
        const fullPath = constructFullPath(value, baseURL, extension);
        setMediaSrc(fullPath);
        setMediaError(false);
        if (isBlob) {
          setIsBlob(false);
          if (blobUrlRef.current) {
            URL.revokeObjectURL(blobUrlRef.current);
            blobUrlRef.current = null;
          }
        }
      }
    } else if (!isBlob) {
      setInputValue("");
      setMediaSrc("");
      setMediaError(false);
    }
  }, [value, baseURL, extension]);

  const constructFullPath = (value: string, baseURL: string, extension: string): string => {
    if (value.startsWith("blob:") || value.startsWith("http://") || value.startsWith("https://")) {
      return value;
    }
    let fullPath = value;
    if (!value.startsWith("/")) {
      fullPath = `${baseURL}${value}`;
    }
    if (!value.includes(".")) {
      fullPath += extension || '.webp';
    }
    return fullPath;
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size === 0) {
      console.error("Le fichier est vide");
      return;
    }

    // `accept="image/*"` ne filtre que la boîte de dialogue : « Tous les fichiers » la
    // contourne. On refuse ici, AVANT de fabriquer le blob — sinon l'aperçu montrerait
    // un fichier que le parent ne recevra jamais. Le type déclaré par le navigateur fait
    // foi ; l'extension ne sert que lorsqu'il est vide.
    const estUneImage = file.type ? file.type.startsWith('image/')
                                  : getMediaType(file.name) === 'Image';
    if (!estUneImage) {
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (onFileRejected) onFileRejected(file);
      return;
    }

    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
    }

    const objectURL = URL.createObjectURL(file);
    blobUrlRef.current = objectURL;

    setInputValue(file.name);
    setMediaSrc(objectURL);
    setIsBlob(true);
    setMediaError(false);

    if (onChange) onChange(event, file);

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleTextChange = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;
    setInputValue(input);
    setMediaError(false);

    if (isBlob) {
      setIsBlob(false);
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    }

    setMediaSrc(input ? constructFullPath(input, baseURL, extension) : "");

    if (onChange) onChange(event, input);
  };

  const handleUploadClick = () => {
    if (readOnly) return;
    fileInputRef.current?.click();
  };

  const handleDeleteClick = () => {
    if (readOnly) return;

    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }

    setMediaSrc("");
    setInputValue("");
    setIsBlob(false);
    setMediaError(false);

    if (fileInputRef.current) fileInputRef.current.value = "";

    if (onDelete) onDelete();
  };

  const handlePreviewClick = () => {
    if (onPreviewClick) onPreviewClick();
    else handleUploadClick();
  };

  const handleMediaError = () => setMediaError(true);

  // ── Déplacement (drag) et redimensionnement (ancre bas-droite) des surimpressions ──
  useEffect(() => {
    if (!dragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const rect = previewBoxRef.current?.getBoundingClientRect();
      if (!rect || !rect.width || !rect.height) return;

      const scaleX = referenceWidth / rect.width;
      const scaleY = referenceHeight / rect.height;
      const dxScreen = e.clientX - dragging.startClientX;
      const dyScreen = e.clientY - dragging.startClientY;

      if (dragging.mode === 'move') {
        let newX = Math.round(clamp(dragging.startX + dxScreen * scaleX, 0, referenceWidth));
        let newY = Math.round(clamp(dragging.startY + dyScreen * scaleY, 0, referenceHeight));

        // Aimantation sur le centre de l'aperçu : on compare le CENTRE de l'élément (sa position
        // plus la moitié de sa taille affichée) à celui du cadre, à 8 px d'écran près.
        if (snapEnabled) {
          const el = previewBoxRef.current?.querySelector<HTMLElement>(`[data-overlay="${dragging.key}"]`);
          const w = (el?.offsetWidth  ?? 0) * scaleX;
          const h = (el?.offsetHeight ?? 0) * scaleY;
          const snapX = Math.abs(newX + w / 2 - referenceWidth  / 2) <= 8 * scaleX;
          const snapY = Math.abs(newY + h / 2 - referenceHeight / 2) <= 8 * scaleY;
          if (snapX) newX = Math.round(referenceWidth  / 2 - w / 2);
          if (snapY) newY = Math.round(referenceHeight / 2 - h / 2);
          setSnap({ x: snapX, y: snapY });
        }

        dragging.onMove?.(newX, newY);
      } else {
        const delta = ((dxScreen * scaleX) + (dyScreen * scaleY)) / 2;
        const newSize = Math.round(clamp(dragging.startSize + delta, 8, 400));
        dragging.onResize?.(newSize);
      }
    };

    const handleMouseUp = () => { setDragging(null); setSnap(null); };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging, referenceWidth, referenceHeight, snapEnabled]);

  const startDrag = (key: 'title' | 'date', mode: 'move' | 'resize', overlay: MediaOverlayText) =>
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onSelectedOverlayChange?.(key);
      const canDrag = mode === 'move' ? !!overlay.onMove : !!overlay.onResize;
      if (!canDrag) return;
      setDragging({
        key, mode,
        startClientX: e.clientX,
        startClientY: e.clientY,
        startX:       overlay.x,
        startY:       overlay.y,
        startSize:    overlay.size,
        onMove:       overlay.onMove,
        onResize:     overlay.onResize,
      });
    };

  // Position/échelle des surimpressions Nom / Heure, calculées relativement à la
  // largeur affichée de l'aperçu (l'heure n'est calculée qu'une seule fois : pas de rafraîchissement par seconde).
  const renderOverlay = (overlay: MediaOverlayText, key: 'title' | 'date') => {
    if (!overlay.visible || !overlay.text) return null;

    const previewWidthNum = (stacked && measuredWidth > 0) ? measuredWidth : (parseFloat(previewWidth || '300') || 300);
    const scale = previewWidthNum / referenceWidth;
    const fontSizePx = Math.max(6, overlay.size * scale);
    const boxOpacity = Math.max(0, Math.min(100, overlay.boxOpacity ?? 0));
    const boxPad = 5 * scale;
    const isEditable = !!(overlay.onMove || overlay.onResize);
    const isSelected = selectedOverlay === key;

    return (
      <div
        key={key}
        data-overlay={key}
        tabIndex={isEditable ? 0 : undefined}
        onKeyDown={(e) => {
          // Flèches : 1 px de la mire (10 avec Maj), sans souris.
          const step = e.shiftKey ? 10 : 1;
          const d = e.key === 'ArrowLeft' ? [-step, 0] : e.key === 'ArrowRight' ? [step, 0]
                  : e.key === 'ArrowUp'   ? [0, -step] : e.key === 'ArrowDown'  ? [0, step] : null;
          if (!d || !overlay.onMove) return;
          e.preventDefault();
          overlay.onMove(Math.round(clamp(overlay.x + d[0], 0, referenceWidth)), Math.round(clamp(overlay.y + d[1], 0, referenceHeight)));
        }}
        onMouseDown={(e) => { startDrag(key, 'move', overlay)(e); (e.currentTarget as HTMLElement).focus?.(); }}
        onClick={(e) => e.stopPropagation()}
        style={{
          position:      'absolute',
          left:          `${(overlay.x / referenceWidth) * 100}%`,
          top:           `${(overlay.y / referenceHeight) * 100}%`,
          display:       'inline-block',
          cursor:        isEditable ? 'move' : 'default',
          userSelect:    'none',
          outline:       isSelected ? (stacked ? '2px dashed #6BA3F5' : '1px dashed rgba(255,255,255,0.5)') : 'none',
          outlineOffset: '2px',
        }}
      >
        <span
          style={{
            fontFamily:     overlay.font,
            color:          overlay.color,
            fontSize:       `${fontSizePx}px`,
            fontWeight:     overlay.bold ? 700 : 400,
            fontStyle:      overlay.italic ? 'italic' : 'normal',
            textDecoration: overlay.underline ? 'underline' : 'none',
            lineHeight: 1,
            whiteSpace: 'nowrap',
            // Même fond que le drawtext du microservice (boxcolor=black@…, boxborderw=5 px sur 1920) ;
            // la marge négative garde le texte à la position x/y.
            display:    'inline-block',
            padding:    boxPad,
            margin:     -boxPad,
            background: boxOpacity > 0 ? `rgba(0,0,0,${boxOpacity / 100})` : 'transparent',
            textShadow: '0 1px 3px rgba(0,0,0,0.8)',
          }}
        >
          {overlay.text}
        </span>
        {isSelected && overlay.onResize && (
          <div
            onMouseDown={startDrag(key, 'resize', overlay)}
            style={{
              position:     'absolute',
              right:        stacked ? -8 : -6,
              bottom:       stacked ? -8 : -6,
              width:        stacked ? 14 : 10,
              height:       stacked ? 14 : 10,
              borderRadius: stacked ? 3 : 2,
              background:   stacked ? '#6BA3F5' : '#fff',
              border:       stacked ? '2px solid #fff' : '1px solid #333',
              cursor:       'nwse-resize',
            }}
            title="Redimensionner"
          />
        )}
      </div>
    );
  };

  const renderViewer = (): JSX.Element | null => {
    const srcToDisplay = isBlob ? mediaSrc : mediaSrc;

    if (!showPreview) return null;

    const containerStyle: React.CSSProperties = {
      width:        previewWidth  || "100%",
      height:       previewHeight || "auto",
      maxWidth:     "100%",
      position:     'relative',
      borderRadius: '5px',
      overflow:     'hidden',
      aspectRatio:  previewHeight ? undefined : `${referenceWidth} / ${referenceHeight}`,
      backgroundColor: 'var(--color-Field-BG-normal)',
      cursor:       previewClickable ? 'pointer' : 'default',
    };

    const imgSrc = srcToDisplay || (previewAlwaysShown ? defaultImage : undefined);

    if (!imgSrc) {
      if (!previewAlwaysShown) return null;
      return (
        <div
          style={{ ...containerStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-Field-FG-normal)', fontSize: '14px', textAlign: 'center', padding: '20px' }}
          onClick={previewClickable ? handlePreviewClick : undefined}
        >
          Aucun média sélectionné
        </div>
      );
    }

    if (mediaError) {
      return (
        <div style={{ padding: '16px', color: '#d32f2f', background: '#ffebee', borderRadius: '5px' }}>
          Impossible de charger le média
        </div>
      );
    }

    return (
      <div ref={previewBoxRef} style={containerStyle} onClick={previewClickable ? handlePreviewClick : undefined}>
        <img
          src={imgSrc}
          alt="Preview"
          style={{ width: '100%', height: '100%', objectFit: previewFit, display: 'block' }}
          onError={handleMediaError}
        />
        {renderOverlay(title, 'title')}
        {renderOverlay(date, 'date')}
        {stacked && (showGuides || dragging) && (
          <div className={mp.guides} aria-hidden="true">
            {showGuides && <div className={mp.safeZone} />}
            <div className={classNames(mp.guideV, snap?.x && mp.guideSnap)} />
            <div className={classNames(mp.guideH, snap?.y && mp.guideSnap)} />
          </div>
        )}
        {stacked && !mediaSrc && !readOnly && (
          <div className={mp.emptyRibbon} onClick={(e) => { e.stopPropagation(); handleUploadClick(); }}>
            Image par défaut · cliquer pour choisir la vôtre ({referenceWidth}×{referenceHeight})
          </div>
        )}
      </div>
    );
  };

  const hasOverlayToggles = !!(onTitleVisibleChange || onDateVisibleChange);

  const renderInputSection = () => (
    <>
      <input
        ref={fileInputRef}
        type="file"
        id={fileInputId.current}
        style={{ display: 'none' }}
        accept="image/*"
        onChange={handleFileChange}
        disabled={readOnly}
      />

      <div
        className={classNames(styles.field_with_icon)}
        style={hasOverlayToggles ? { position: 'relative', width: '100%', height: '100%', justifyContent: 'center', alignItems: 'flex-start' } : undefined}
      >
        {showTextInput && (
          <input
            type="text"
            className={classNames(styles.field_input)}
            placeholder={placeholder}
            value={typeof inputValue === "string" ? inputValue : ""}
            readOnly={readOnly}
            onChange={handleTextChange}
          />
        )}
        {!readOnly && upload && (
          <div className={classNames(styles.icon_square)} onClick={handleUploadClick}>
            <Icon name="upload" color="var(--color-Field-Icon-normal)" hoverColor="var(--color-Field-Icon-hover-normal)" width={18} height={18} />
          </div>
        )}
        {!readOnly && onDelete && (
          <div className={classNames(styles.icon_square)} onClick={handleDeleteClick}>
            <Icon name="trashCan" color="var(--color-Field-Icon-normal)" hoverColor="var(--color-Field-Icon-hover-normal)" width={18} height={18} />
          </div>
        )}

        {hasOverlayToggles && (
          <div className={classNames(styles.overlay_toggle_block)}>
            {onTitleVisibleChange && (
              <label className={classNames(styles.overlay_toggle_item)}>
                <span>{titleToggleLabel}</span>
                <Toggle checked={title.visible} onChange={onTitleVisibleChange} form="switch" />
              </label>
            )}
            {onDateVisibleChange && (
              <label className={classNames(styles.overlay_toggle_item)}>
                <span>{dateToggleLabel}</span>
                <Toggle checked={date.visible} onChange={onDateVisibleChange} form="switch" />
              </label>
            )}
          </div>
        )}
      </div>
    </>
  );

  // ── Disposition « stacked » : cadre de contrôle, pastilles, repères ──
  const renderStacked = () => {
    const r = referenceWidth / referenceHeight;
    const ratioLabel = Math.abs(r - 16 / 9) < 0.01 ? '16:9' : Math.abs(r - 9 / 16) < 0.01 ? '9:16' : '';
    const chip = (key: 'title' | 'date', label: string, overlay: MediaOverlayText, onVisible?: (v: boolean) => void) =>
      onVisible ? (
        <button type="button" aria-pressed={overlay.visible}
                className={classNames(mp.chip, overlay.visible && mp.chipOn)}
                onClick={() => { onVisible(!overlay.visible); if (!overlay.visible) onSelectedOverlayChange?.(key); }}>
          <span className={mp.chipDot} />{label}
        </button>
      ) : null;

    return (
      <div className={mp.frame}>
        <input ref={fileInputRef} type="file" id={fileInputId.current} style={{ display: 'none' }}
               accept="image/*" onChange={handleFileChange} disabled={readOnly} />
        <div className={mp.frameHead}>
          <span className={mp.formatPill}>{ratioLabel && `${ratioLabel} · `}{referenceWidth} × {referenceHeight}</span>
          <button type="button" aria-pressed={showGuides}
                  className={classNames(mp.chip, showGuides && mp.chipOn)}
                  title="Afficher le centre et la zone sûre (5 %)"
                  onClick={() => setShowGuides(v => !v)}>
            <span className={mp.chipDot} />Repères
          </button>
        </div>
        <div className={mp.stage}>{renderViewer()}</div>
        <div className={mp.frameFoot}>
          {chip('title', titleToggleLabel, title, onTitleVisibleChange)}
          {chip('date',  dateToggleLabel,  date,  onDateVisibleChange)}
          <span className={mp.spacer} />
          {fileName && <span className={mp.fileName} title={fileName}>{fileName}</span>}
          {!readOnly && upload && (
            <button type="button" className={mp.smallBtn} onClick={handleUploadClick}>Remplacer</button>
          )}
          {!readOnly && onDelete && mediaSrc && (
            <button type="button" className={classNames(mp.smallBtn, mp.smallBtnDanger)} onClick={handleDeleteClick}>Supprimer</button>
          )}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (stacked) return renderStacked();
    const isLeftPosition = previewPosition?.startsWith('left');
    const isRightPosition = previewPosition?.startsWith('right');

    if (isLeftPosition || isRightPosition) {
      let alignItems: 'flex-start' | 'center' | 'flex-end' | 'stretch' = 'flex-start';
      if (previewPosition?.endsWith('_center')) alignItems = 'center';
      else if (previewPosition?.endsWith('_bottom')) alignItems = 'flex-end';

      // Le bloc de toggles "Nom"/"Date" se positionne en absolu, centré sur la hauteur
      // de l'aperçu : il faut donc que la colonne des icônes soit étirée sur cette hauteur.
      if (hasOverlayToggles) alignItems = 'stretch';

      return (
        <div style={{ display: 'flex', flexDirection: isLeftPosition ? 'row' : 'row-reverse', gap: '16px', alignItems, width: '100%' }}>
          {/* Le nom coiffe l'aperçu : voir FieldMedia, même raison. */}
          <div style={{ flex: previewWidth ? `0 0 ${previewWidth}` : '0 0 300px', minWidth: 0 }}>
            {fileName && (
              <div className={classNames(styles.file_name)} title={fileName}>{fileName}</div>
            )}
            {renderViewer()}
          </div>
          <div style={{ flex: '1', minWidth: 0 }}>
            {renderInputSection()}
          </div>
        </div>
      );
    }

    return (
      <>
        {renderInputSection()}
        {fileName && (
          <div className={classNames(styles.file_name)} title={fileName}>{fileName}</div>
        )}
        {renderViewer()}
      </>
    );
  };

  return (
    <div className={classNames(styles.field)} style={{ width, height, ...fieldStyle }}>
      <div className={classNames(styles.file_input_wrapper)}>
        {renderContent()}
      </div>
    </div>
  );
};

export default FieldMediaPendingImage;
